<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted } from 'vue';
  import { pageOrientation } from '@record-fill';
  import { useFormBuilderStore } from '../../model/form-builder.store';
  import type { FormStructure } from '../../model/form.types';
  import FormCanvas from './FormCanvas.vue';

  interface Props {
    structure: FormStructure;
    zoom: number;
    viewScrollTop?: number;
    /** Makes every input inert, whatever `properties.editable` says on each element. */
    forceReadonly?: boolean;
    /**
     * Hands the store back to the local draft of whoever is building forms, and lifts the
     * persistence suspension this stage turned on. Off by default so the screens that
     * mounted this stage before keep behaving the same way.
     */
    restoreDraftOnUnmount?: boolean;
    /**
     * Anchor field ids of this form, handed down instead of read from the shared rules store.
     * Absent keeps anchor marking off, which is how the screens that mounted this stage before
     * behave today.
     */
    anchorFieldIds?: string[];
    /** Draws the anchor badge on every field of `anchorFieldIds`. */
    showAnchorHighlights?: boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    'update:zoom': [value: number];
    'update:pageCount': [value: number];
    'update:currentPage': [value: number];
  }>();

  const formBuilderStore = useFormBuilderStore();
  const orientation = computed(() => pageOrientation(props.structure));

  onMounted(() => {
    // Suspending first is mandatory: loading the canvas with persistence live would write this
    // form over the local draft of whoever is building forms.
    formBuilderStore.setLocalStorageSuspended(true);
    formBuilderStore.loadFormStructure({
      elements: props.structure.elements,
      version: props.structure.version,
    });
  });

  onBeforeUnmount(() => {
    if (!props.restoreDraftOnUnmount) return;

    formBuilderStore.initForCreateMode();
  });
</script>

<template>
  <div class="flex min-w-0 flex-col" data-testid="form-builder-viewer-stage">
    <FormCanvas
      :orientation="orientation"
      :zoom="props.zoom"
      :view-scroll-top="props.viewScrollTop"
      :force-readonly="props.forceReadonly"
      :anchor-field-ids="props.anchorFieldIds"
      :show-anchor-highlights="props.showAnchorHighlights"
      interaction-mode="view"
      @update:zoom="emit('update:zoom', $event)"
      @update:page-count="emit('update:pageCount', $event)"
      @update:current-page="emit('update:currentPage', $event)"
    />
  </div>
</template>
